import { useEffect, ReactNode } from 'react';
import { useLayoutStore } from '@modules/core/store';
import { Flex } from '@huspy/briks-web';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import { ProposalCreationSlido } from '@modules/proposals/presentation/v2/pages/List/features/ProposalCreationSlido';
import MainContentPageWrapper from '@components/v2/MainContentPageWrapper';
import { ControlPanel } from '@modules/proposals/presentation/v2/pages/List/features/ControlPanel';
import { ProposalsTableAE } from '@modules/proposals/presentation/v2/pages/List/features/ProposalsTable/ae';
import { ProposalsTableES } from '@modules/proposals/presentation/v2/pages/List/features/ProposalsTable/es';
import { EmptyTemplateComponent } from '@modules/core/components/EmptyTemplateComponent';
import { ProposalsListRoute } from '@modules/proposals/presentation/routes';
import { useProposalsListAE } from '@modules/proposals/api/query/useProposalsListAE';
import { useProposalsListES } from '@modules/proposals/api/query/useProposalsListES';
import { useTranslation } from 'react-i18next';
import { InfoWidget } from '@components/v2/InfoWidget';
import { IS_UAE_ENV } from '@modules/core/utils';

const PROPOSALS_PAGE_SIZE = 7;

type ProposalsHeaderProps = {
  isLoading: boolean;
  total?: number;
};

type EmptyProposalsProps = {
  creationSlidoButton: ReactNode | null;
};

type ProposalsListComponentProps = {
  creationSlidoButton: ReactNode | null;
};

const useCreationSlidoButton = (): ReactNode | null => {
  const { isFeatureActive, isFeatureFlagsLoading } = useFeatureFlags();

  if (isFeatureFlagsLoading || !isFeatureActive('proposals.module')) {
    return null;
  }

  return <ProposalCreationSlido />;
};

const ProposalsHeader = ({ isLoading, total }: ProposalsHeaderProps) => {
  const { t } = useTranslation();

  return (
    <Flex align='center' gap={ 3 }>
      <InfoWidget
        title={ t('proposals.totalCount') }
        isLoading={ isLoading }
        content={ total }
      />
    </Flex>
  );
};

const EmptyProposals = ({ creationSlidoButton }: EmptyProposalsProps) => {
  const { t } = useTranslation();

  return (
    <EmptyTemplateComponent
      title={ t('proposals.emptyTemplate.title') }
      description={ t('proposals.emptyTemplate.description') }
      cta={ creationSlidoButton }
    />
  );
};

const ProposalsListAE = ({ creationSlidoButton }: ProposalsListComponentProps) => {
  const { t } = useTranslation();
  const {
    page, q, sort, order,
  } = ProposalsListRoute.useSearch();

  const { data, isLoading, error } = useProposalsListAE({
    page,
    q,
    sort,
    order,
    limit: PROPOSALS_PAGE_SIZE,
  });

  const hasProposals = !isLoading && data && data.total > 0;
  const totalCount = data?.total_unfiltered_records;

  return (
    <MainContentPageWrapper
      title={ t('proposals.title') }
      subtitle={ t('proposals.subtitle') }
      asideTitleContent={ <ProposalsHeader isLoading={ isLoading } total={ totalCount } /> }
    >
      <Flex justify='space-between' pt='6' pb='10'>
        <ControlPanel />
      </Flex>

      {(isLoading || hasProposals) && (
        <ProposalsTableAE data={ data } isLoading={ isLoading } error={ error } />
      )}

      {!isLoading && !hasProposals && (
        <EmptyProposals creationSlidoButton={ creationSlidoButton } />
      )}
    </MainContentPageWrapper>
  );
};

const ProposalsListES = ({ creationSlidoButton }: ProposalsListComponentProps) => {
  const { t } = useTranslation();
  const { page } = ProposalsListRoute.useSearch();

  const { data, isLoading } = useProposalsListES({
    page,
    limit: PROPOSALS_PAGE_SIZE,
  });

  const hasProposals = !isLoading && data && data.total > 0;
  const totalCount = data?.total_unfiltered_records;

  return (
    <MainContentPageWrapper
      title={ t('proposals.title') }
      subtitle={ t('proposals.subtitle') }
      asideTitleContent={ <ProposalsHeader isLoading={ isLoading } total={ totalCount } /> }
    >
      {(isLoading || hasProposals) && (
        <ProposalsTableES data={ data } isLoading={ isLoading } />
      )}

      {!isLoading && !hasProposals && (
        <EmptyProposals creationSlidoButton={ creationSlidoButton } />
      )}
    </MainContentPageWrapper>
  );
};

export const ProposalsList = () => {
  const { setHeaderContent } = useLayoutStore();
  const creationSlidoButton = useCreationSlidoButton();

  useEffect(() => {
    setHeaderContent(creationSlidoButton);
    return () => {
      setHeaderContent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Header is not added as dependency due to call-stack error
  }, [setHeaderContent]);

  const ProposalsListComponent = IS_UAE_ENV ? ProposalsListAE : ProposalsListES;
  return <ProposalsListComponent creationSlidoButton={ creationSlidoButton } />;
};
